import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { buildAuthModalPath, performLogout } from '../lib/utils'
import { Person, Shield, CreditCard, Notifications, IntegrationInstructions, SettingsSuggest, Logout } from '@mui/icons-material'
import { Header } from './Header'

interface SettingsLayoutProps {
  children: React.ReactNode
}

export const SettingsLayout: React.FC<SettingsLayoutProps> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useLanguage()

  const menuItems = [
    { path: '/settings/general', icon: <Person fontSize="small" />, label: t('settings.general') },
    { path: '/settings/security', icon: <Shield fontSize="small" />, label: t('settings.security') },
    { path: '/settings/preferences', icon: <SettingsSuggest fontSize="small" />, label: t('settings.preferences') },
    { path: '/settings/billing', icon: <CreditCard fontSize="small" />, label: t('settings.billing') },
    { path: '/settings/notifications', icon: <Notifications fontSize="small" />, label: t('settings.notifications') },
    { path: '/settings/integrations', icon: <IntegrationInstructions fontSize="small" />, label: t('settings.integrations') },
  ]

  // Check if billing related sub-pages are active
  const isBillingActive =
    location.pathname === '/settings/billing' ||
    location.pathname === '/settings/credits' ||
    location.pathname === '/settings/subscription'

  const authTarget = buildAuthModalPath(location.pathname, location.search, 'login')

  const handleSignOut = async () => {
    await performLogout()
    navigate(authTarget)
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          <aside className="w-[240px] shrink-0 hidden lg:block">
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden sticky top-24">
              <div className="py-4">
                <nav className="space-y-1">
                  {menuItems.map((item) => {
                    const isActive = location.pathname === item.path || (item.path === '/settings/billing' && isBillingActive)
                    return (
                      <button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        className={`flex w-full items-center gap-3 px-6 py-3 text-sm font-medium transition-all ${
                          isActive
                            ? 'bg-blue-50 text-primary border-r-4 border-primary'
                            : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                        }`}
                      >
                        <span className={isActive ? 'text-primary' : 'text-slate-400'}>{item.icon}</span>
                        {item.label}
                      </button>
                    )
                  })}
                </nav>
              </div>
              <div className="p-4 border-t border-slate-100">
                <button
                  data-auth-target={authTarget}
                  onClick={handleSignOut}
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                >
                  <Logout className="text-rose-500" fontSize="small" />
                  {t('profile.signOut')}
                </button>
              </div>
            </div>
          </aside>

          <main className="flex-1 min-w-0">
            {children}
          </main>
        </div>
      </div>
    </div>
  )
}
